import React, { useEffect, useState } from 'react'
import { FolderPlus, Trash2, Languages } from 'lucide-react'
import Button from './ui/Button'
import {
  useProjects,
  useCreateProject,
  useSetProjectLanguage,
  useDeleteProject,
} from '../hooks/useRag'

// Project language options (code → label). 'auto' = no fixed language.
export const LANGS = [
  ['auto', 'Auto / mixed'],
  ['en', 'English'], ['cs', 'Czech'], ['de', 'German'], ['fr', 'French'],
  ['es', 'Spanish'], ['it', 'Italian'], ['pl', 'Polish'], ['pt', 'Portuguese'],
  ['ru', 'Russian'], ['uk', 'Ukrainian'], ['nl', 'Dutch'],
]
export const langLabel = (code) => LANGS.find(([c]) => c === code)?.[1] || code || 'Auto / mixed'

const selectCls =
  'h-11 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'

// One project bar shared by every Sources mode (Library, Files, Media) so the
// project is picked once and the controls are identical everywhere. Controlled:
// the parent owns `project` and gets changes via `onProjectChange`.
export default function ProjectBar({ project, onProjectChange }) {
  const projectsQuery = useProjects()
  const createProject = useCreateProject()
  const setProjectLanguage = useSetProjectLanguage()
  const deleteProject = useDeleteProject()
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newLang, setNewLang] = useState('auto')

  useEffect(() => {
    if (!project && projectsQuery.data?.current_project) onProjectChange(projectsQuery.data.current_project)
  }, [project, projectsQuery.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const projects = projectsQuery.data?.projects ?? []
  const projLang = projects.find((p) => p.name === project)?.language || 'auto'

  const submitNew = (e) => {
    e.preventDefault()
    const name = newName.trim()
    if (!name) return
    createProject.mutate(
      { name, set_as_current: false, language: newLang },
      { onSuccess: () => { onProjectChange(name); setCreating(false); setNewName(''); setNewLang('auto') } }
    )
  }
  const onDelete = () => {
    if (!project) return
    const ok = window.confirm(
      `Delete project "${project}" and everything it owns — its documents, vector index ` +
        `and Q&A history, plus any transcribe channels it created. This cannot be undone.`
    )
    if (!ok) return
    deleteProject.mutate(project, { onSuccess: () => onProjectChange('') })
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <select value={project} onChange={(e) => onProjectChange(e.target.value)} className={selectCls}>
          {projectsQuery.isLoading && <option>Loading…</option>}
          {!project && <option value="">Select a project…</option>}
          {projects.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name} · {p.documents_count} docs · {p.chunks_count} chunks
            </option>
          ))}
        </select>

        {creating ? (
          <form onSubmit={submitNew} className="flex items-center gap-2">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New project name"
              className="h-11 rounded-lg px-3 text-sm bg-white border border-gold/60 text-ink focus:outline-none focus:ring-2 focus:ring-spark/40 dark:bg-midnight-800 dark:border-midnight-600 dark:text-mist"
            />
            <select value={newLang} onChange={(e) => setNewLang(e.target.value)} title="Project language" className="h-11 rounded-lg px-2 text-sm bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist">
              {LANGS.map(([c, label]) => <option key={c} value={c}>{label}</option>)}
            </select>
            <Button type="submit" size="sm" loading={createProject.isPending}>Create</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
          </form>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <FolderPlus className="w-4 h-4" /> New project
          </Button>
        )}

        {project && !creating && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDelete}
            loading={deleteProject.isPending}
            className="text-red-600 border-red-300/70 hover:bg-red-50 dark:text-red-300 dark:border-red-800/60 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4" /> Delete project
          </Button>
        )}

        {project && !creating && (
          <label className="flex items-center gap-1.5 text-xs text-ink-soft dark:text-mist/60 ml-auto">
            <Languages className="w-3.5 h-3.5" /> Language
            <select
              value={projLang}
              onChange={(e) => setProjectLanguage.mutate({ project, language: e.target.value })}
              disabled={setProjectLanguage.isPending}
              className="h-8 rounded-lg px-2 text-xs bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
            >
              {LANGS.map(([c, label]) => <option key={c} value={c}>{label}</option>)}
            </select>
          </label>
        )}
      </div>

      {createProject.isError && <p className="text-sm text-red-600">{createProject.error?.message}</p>}
      {deleteProject.isError && <p className="text-sm text-red-600">{deleteProject.error?.message}</p>}
      {deleteProject.isSuccess && (
        <p className="text-sm text-emerald-700 dark:text-emerald-300">
          Deleted “{deleteProject.variables}” — {(deleteProject.data?.deleted ?? []).length} resource(s) removed.
        </p>
      )}
    </div>
  )
}
